import { Box, Button, Flex, Text, Tooltip } from '@modusign/design-system';
import { useTranslation } from 'react-i18next';

import { ModalContentProps } from '@app/features/apply-react-signing/components/AttachmentListModal';
import { getFormattedFilename } from '@app/features/apply-react-signing/libs/formatter/getFormattedFilename';

import MobileAttachmentListItem from './MobileAttachmentListItem';

export default function MobileAttachmentList({
  attachments,
  enableDownload,
  isDownloading,
  onDownloadAttachmentBundleClick,
  onDownloadAttachmentClick,
  onOpenAttachmentClick,
  isDocumentCompleted,
}: ModalContentProps) {
  const { t } = useTranslation();
  const signerAttachments = attachments.filter((a) => a.participantReference.type === 'SIGNER');
  const requesterAttachments = attachments.filter(
    (a) => a.participantReference.type === 'REQUESTER'
  );

  return (
    <Box maxHeight="500px" overflow="auto" width="100%">
      {!!requesterAttachments.length && (
        <Box>
          <Flex
            alignItems="center"
            borderBottom="1px solid"
            borderBottomColor="tableBorderNeutralBase"
            justifyContent="space-between"
            px={6}
            py={4}
          >
            <Text as="th" color="textNeutralVeryLight" fontWeight="medium" textAlign="left">
              {t('DocumentAttachmentListModal-requester')}
            </Text>
            {enableDownload && (
              <Button
                height="30px"
                isLoading={isDownloading}
                px="16px"
                sx={{ position: 'relative' }}
                variant="subtle"
                onClick={() => onDownloadAttachmentBundleClick('REQUESTER')}
              >
                {t('DocumentAttachmentListModal-bundleDownload')}
              </Button>
            )}
          </Flex>
          <ul>
            {requesterAttachments.map((item, index) => (
              <MobileAttachmentListItem
                attachmentType={item.title}
                enableDownload={enableDownload}
                fileName={getFormattedFilename(item.file)}
                key={item.id}
                participantName={item.participantReference.name || ''}
                onDownload={() => onDownloadAttachmentClick(item, index)}
                onOpen={() => onOpenAttachmentClick(item, index)}
              />
            ))}
          </ul>
        </Box>
      )}
      {!!(signerAttachments.length && requesterAttachments.length) && (
        <Box
          bg="dividerNeutralLight"
          borderBottom="1px solid"
          borderBottomColor="lightGray500"
          height="12px"
        />
      )}
      {!!signerAttachments.length && (
        <Box>
          <Flex
            alignItems="center"
            borderBottom="1px solid"
            borderBottomColor="tableBorderNeutralBase"
            justifyContent="space-between"
            px={6}
            py={4}
          >
            <Text as="th" color="textNeutralVeryLight" fontWeight="medium" textAlign="left">
              {t('DocumentAttachmentListModal-signer')}
            </Text>
            {enableDownload && (
              <Tooltip
                content={t('DocumentAttachmentListModal-bundleDownloadTooltip')}
                disabled={isDocumentCompleted}
                placement="left"
              >
                <Button
                  disabled={!isDocumentCompleted}
                  height="30px"
                  isLoading={isDownloading}
                  px="16px"
                  sx={{ position: 'relative' }}
                  variant="subtle"
                  onClick={() => onDownloadAttachmentBundleClick('SIGNER')}
                >
                  {t('DocumentAttachmentListModal-bundleDownload')}
                </Button>
              </Tooltip>
            )}
          </Flex>
          <ul>
            {signerAttachments.map((item, index) => (
              <MobileAttachmentListItem
                attachmentType={item.title}
                enableDownload={enableDownload}
                fileName={getFormattedFilename(item.file)}
                key={item.id}
                participantName={item.participantReference.name || ''}
                onDownload={() => onDownloadAttachmentClick(item, index)}
                onOpen={() => onOpenAttachmentClick(item, index)}
              />
            ))}
          </ul>
        </Box>
      )}
    </Box>
  );
}
